import { useEffect, useState } from "react";
import axios from "axios";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { API } from "@/context/AuthContext";
import { inr, inrShort } from "@/pages/Dashboard";

const fyStart = () => { const n = new Date(); const y = n.getMonth() >= 3 ? n.getFullYear() : n.getFullYear() - 1; return `${y}-04`; };
const RANGES = [["fy", `This FY (${fyStart()} →)`], ["12", "12 months"], ["all", "All history"]];

/** Month-end closing balance history of the Input-GST accounts — how the credit builds up (or gets utilised) over time. */
export function GSTR2GLHistory({ refreshKey }) {
  const [months, setMonths] = useState("fy");
  const [d, setD] = useState(null);
  useEffect(() => {
    const params = months === "fy" ? { from_month: fyStart() } : months === "all" ? { months: 60 } : { months };
    axios.get(`${API}/reports/gstr2/gl-balance/history`, { params }).then((r) => setD(r.data)).catch(() => {});
  }, [months, refreshKey]);
  const rows = d?.rows || [];
  const chart = rows.map((r) => ({ month: r.month, closing: r.input_closing, availed: r.input_debit, utilised: -r.input_credit, rcm: -r.rcm_closing }));
  const last = rows[rows.length - 1];
  return (
    <Card className="border-slate-200 shadow-sm fade-in-up" data-testid="gstr2-gl-history">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div>
            <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Input-GST credit build-up — month-end balances</p>
            <p className="text-[11px] text-slate-500">{d ? <>{months === "fy" ? `From ${fyStart()} (current financial year)` : `Ledger history from ${d.all_time_from}`} · closing balance of the three Input accounts at each month-end, with credit availed (debits) and utilisation / reversals (credits) in that month</> : "Loading…"}</p>
          </div>
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs">
            {RANGES.map(([k, l]) => <button key={k} type="button" onClick={() => setMonths(k)} data-testid={`gstr2-history-range-${k}`} className={`px-3 py-1.5 ${months === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>)}
          </div>
        </div>
        {last && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3" data-testid="gstr2-history-kpis">
            {[["Input credit now", inrShort(last.input_closing), `${last.month} closing`], ["Change over period", `${last.input_closing - (d.opening_input ?? 0) >= 0 ? "+" : "−"}${inrShort(Math.abs(last.input_closing - (d.opening_input ?? 0)))}`, `opening ${inrShort(d.opening_input ?? 0)} → ${last.month}`],
              ["Avg availed / month", inrShort(rows.reduce((s, r) => s + r.input_debit, 0) / rows.length), "debits on Input a/cs"], ["RCM liability now", inrShort(-last.rcm_closing), `${last.month} closing (credit)`]].map(([l, v, s]) => (
              <div key={l} className="rounded border border-slate-200 bg-slate-50 px-3 py-1.5"><p className="text-[10px] uppercase tracking-wider text-slate-500">{l}</p><p className="text-sm font-black tabular-nums text-slate-900">{v}</p><p className="text-[10px] text-slate-400">{s}</p></div>
            ))}
          </div>
        )}
        {rows.length > 0 && (
          <div className="h-64" data-testid="gstr2-history-chart">
            <ResponsiveContainer>
              <ComposedChart data={chart} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="l" tickFormatter={(v) => inrShort(v)} tick={{ fontSize: 10 }} width={76} />
                <YAxis yAxisId="r" orientation="right" tickFormatter={(v) => inrShort(v)} tick={{ fontSize: 10 }} width={76} />
                <Tooltip formatter={(v, n) => [inr(v), n]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="r" dataKey="availed" name="Credit availed (month)" fill="#10b981" radius={[3, 3, 0, 0]} />
                <Bar yAxisId="r" dataKey="utilised" name="Utilised / reversed (month)" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                <Line yAxisId="l" type="monotone" dataKey="closing" name="Input credit closing balance" stroke="#0f172a" strokeWidth={2.5} dot={{ r: 2.5 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
        {rows.length > 0 && (
          <div className="overflow-x-auto max-h-[320px] overflow-y-auto mt-3">
            <Table data-testid="gstr2-history-table">
              <TableHeader><TableRow className="bg-slate-50">{["Month", "INPUT IGST", "INPUT CGST", "INPUT SGST", "Input total", "Change", "Availed", "Utilised / reversed", "RCM a/cs"].map((h) => <TableHead key={h} className={`text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap ${h === "Month" ? "" : "text-right"}`}>{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {[...rows].reverse().map((r) => (
                  <TableRow key={r.month} className="border-b border-slate-100" data-testid={`gstr2-history-row-${r.month}`}>
                    <TableCell className="text-xs font-semibold text-slate-800">{r.month}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{inr(r.accounts["12605900"].closing)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{inr(r.accounts["12605902"].closing)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{inr(r.accounts["12605901"].closing)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums font-semibold">{inr(r.input_closing)}</TableCell>
                    <TableCell className={`text-xs text-right tabular-nums ${r.input_change >= 0 ? "text-emerald-700" : "text-amber-700"}`}>{r.input_change >= 0 ? "+" : ""}{inr(r.input_change)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums text-slate-600">{inr(r.input_debit)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums text-slate-600">{inr(r.input_credit)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums text-sky-700">{inr(r.rcm_closing)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
